import { useEffect, useRef, useState } from 'react';
import { KEY_GROUPS, type KeyScope } from '../keys';
import { useFocusOnOpen } from '../useFocusOnOpen';

/**
 * What the keys do.
 *
 * Two global shortcuts existed — `/` to focus search and `Escape` to dismiss — and neither was
 * written down anywhere. `/` was discoverable only from a placeholder that vanishes the moment
 * you type into it. A shortcut nobody can find is a shortcut nobody uses, and `?` is the
 * convention for asking.
 */

/**
 * `Anywhere`, then the keys for the screen you are on.
 *
 * The panel used to print every group at once, so a reader on Today scrolled past ten task-list
 * keys to reach the three that applied. The groups themselves live in `keys.ts`, which the list's
 * own hint reads too — the panel and the footer were two documents and disagreed by eight keys.
 */
export function Shortcuts({
  screen,
  open: openProp,
  onOpenChange,
}: {
  screen?: KeyScope;
  /**
   * Driven from outside, so the phone's more menu can be the way in.
   *
   * The `?` button is hidden below 34rem — four icons beside two tabs wrap the nav onto another
   * line and push the first task past the top third of the screen, which is a finding of its own.
   * The panel itself is not hidden: it moves to the menu a phone user already opens to navigate.
   */
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const setOpen = (next: boolean | ((v: boolean) => boolean)) => {
    const value = typeof next === 'function' ? next(open) : next;
    if (onOpenChange) onOpenChange(value);
    else setOwnOpen(value);
  };

  /*
    Whether the panel is open, readable from a listener registered once.

    Escape used to close it unconditionally, which was harmless while nothing else answered
    Escape from the same screen. The full read view answers it now — it leaves the view — so this
    has to say whether it actually consumed the press, and it can only say that if it knows.
  */
  const openNow = useRef(open);
  useEffect(() => {
    openNow.current = open;
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Layered, not swallowed: a press with nothing open belongs to whatever is behind this.
        if (!openNow.current) return;
        e.preventDefault();
        return setOpen(false);
      }
      if (e.key !== '?' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      // Same guard as the other two: never steal a keystroke from a field.
      if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement ||
        (el instanceof HTMLElement && el.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      setOpen((v) => !v);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /*
    The sheet takes focus, keeps Tab, and gives focus back.

    Tab from it walked straight into the page behind — 25 presses probed on 1.82.0, none inside —
    so the panel that explains the keys ignored the keyboard of the user reading it. It holds no
    controls, so the sheet itself takes focus (which also lets the arrow keys scroll it) and Tab
    stays put; Escape or `?` closes it.

    Back to whatever opened it: the `?` button, or the row title a `?` keypress was made from. At
    375 it is opened from the *More* menu, whose item is gone by the time the sheet mounts — so
    with no opener left, focus goes to whichever of the two triggers is drawn at this width.

    A `dialog`, named by its heading. That puts it on `hasOpenSurface`'s list, which is the point:
    one Escape closes the sheet and nothing else — not the selection behind it, not the task panel,
    whose capture-phase Escape used to close the panel and leave the sheet up.
  */
  const sheet = useRef<HTMLDivElement>(null);
  useFocusOnOpen(open, sheet, {
    trap: true,
    returnTo: (opener) =>
      opener ??
      Array.from(
        document.querySelectorAll<HTMLElement>('.shortcuts-hint, .more-menu > button'),
      ).find((el) => el.getClientRects().length > 0) ??
      null,
  });

  return (
    <>
      <button className="link shortcuts-hint" onClick={() => setOpen((v) => !v)} title="Shortcuts">
        ?
      </button>
      {open && (
        <div
          ref={sheet}
          className="card shortcuts"
          data-testid="shortcuts"
          role="dialog"
          aria-labelledby="shortcuts-title"
          tabIndex={-1}
        >
          <h3 id="shortcuts-title">Keys</h3>
          {/*
            Said once, at the top, on the width where it needs saying.

            The panel was hidden entirely below 34rem partly because it documents keys a touch
            device does not have. That is a reason to caption it, not to withhold it: a phone-sized
            window can have a keyboard attached, and the screens least able to explain themselves
            are the ones this was switched off on.
          */}
          <p className="subtext shortcuts-note">These need a keyboard.</p>
          {KEY_GROUPS.filter((group) => group.scope === null || group.scope === screen).map(
            (group) => (
              <div key={group.where} className="shortcut-group">
                <h4>{group.where}</h4>
                <ul className="plain">
                  {group.keys.map(([key, what]) => (
                    <li key={key}>
                      <kbd>{key}</kbd> {what}
                    </li>
                  ))}
                </ul>
              </div>
            ),
          )}
        </div>
      )}
    </>
  );
}
